import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { instance } from '../../axios/AxiosInstance';
import AppLayout from '../../components/AppLayout/AppLayout';
import { getRoleLabel } from '../../utils/roleLabel';
import type { ManagerUserRow } from './ManagerUsers';
import './UserDetails.scss';

const ROLES = ['MANAGER', 'WRITER'] as const;

interface ListResponse {
  data: ManagerUserRow[];
}

export default function UserDetails() {
  const { id } = useParams<{ id: string }>();
  /** Route is `/manager/users/:id` where `id` is `new` or the user's email (URL-encoded). */
  const userEmailParam =
    id !== undefined && id !== 'new' ? decodeURIComponent(id) : undefined;
  const navigate = useNavigate();
  const isEdit = userEmailParam !== undefined;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'WRITER',
  });

  const fetchUserForEdit = useCallback(async () => {
    if (!isEdit || !userEmailParam) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await instance.get<ListResponse>('managers');
      const found = (data.data ?? []).find((u) => String(u.email) === userEmailParam);
      if (found) {
        setForm({
          email: found.email ?? '',
          first_name: found.first_name ?? '',
          last_name: found.last_name ?? '',
          phone: found.phone ?? '',
          role: found.role ?? 'WRITER',
        });
      } else {
        setError('Хэрэглэгч олдсонгүй.');
      }
    } catch {
      setError('Хэрэглэгчийн мэдээлэл ачааллахад алдаа гарлаа.');
    } finally {
      setLoading(false);
    }
  }, [isEdit, userEmailParam]);

  useEffect(() => {
    if (isEdit) {
      fetchUserForEdit();
    }
  }, [isEdit, fetchUserForEdit]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (isEdit && userEmailParam) {
        await instance.put('managers', {
          email: form.email.trim(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: form.phone.trim(),
        });
      } else {
        if (!form.email.trim()) {
          setError('Имэйл оруулна уу.');
          setSaving(false);
          return;
        }
        await instance.post('managers', {
          email: form.email.trim(),
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          phone: form.phone.trim(),
          role: form.role,
        });
      }
      if (window.opener) {
        window.opener.focus();
        window.close();
      } else {
        navigate('/manager/users', { replace: true });
      }
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(msg || (isEdit ? 'Хэрэглэгч шинэчлэхэд алдаа гарлаа.' : 'Хэрэглэгч нэмэхэд алдаа гарлаа.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <div className="user-details">
        <div className="user-details__header">
          <h1 className="user-details__title">{isEdit ? 'Хэрэглэгч засах' : 'Шинэ хэрэглэгч'}</h1>
        </div>

        {error && (
          <p className="user-details__error" role="alert">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-sub">Ачааллаж байна...</p>
        ) : (
          <form className="user-details__form" onSubmit={handleSubmit}>
            {isEdit ? (
              <label>
                <span className="label-text">Имэйл</span>
                <input
                  type="email"
                  className="input user-details__input-readonly"
                  value={form.email}
                  readOnly
                  disabled
                  aria-describedby="email-readonly-hint"
                />
                <span id="email-readonly-hint" className="user-details__hint">
                  Имэйл засагдах боломжгүй
                </span>
              </label>
            ) : (
              <label>
                <span className="label-text">Имэйл</span>
                <input
                  type="email"
                  name="email"
                  className="input"
                  placeholder="name@example.com"
                  value={form.email}
                  onChange={handleChange}
                  disabled={saving}
                  required
                />
              </label>
            )}

            <label>
              <span className="label-text">Нэр</span>
              <input
                type="text"
                name="first_name"
                className="input"
                placeholder="Нэр"
                value={form.first_name}
                onChange={handleChange}
                disabled={saving}
              />
            </label>

            <label>
              <span className="label-text">Овог</span>
              <input
                type="text"
                name="last_name"
                className="input"
                placeholder="Овог"
                value={form.last_name}
                onChange={handleChange}
                disabled={saving}
              />
            </label>

            <label>
              <span className="label-text">Утас</span>
              <input
                type="tel"
                name="phone"
                className="input"
                placeholder="Утасны дугаар"
                value={form.phone}
                onChange={handleChange}
                disabled={saving}
              />
            </label>

            {!isEdit && (
              <label>
                <span className="label-text">Эрх</span>
                <select
                  name="role"
                  className="input user-details__select"
                  value={form.role}
                  onChange={handleChange}
                  disabled={saving}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {getRoleLabel(r)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="user-details__actions">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => (window.opener ? window.close() : navigate('/manager/users'))}
                disabled={saving}
              >
                Цуцлах
              </button>
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? 'Хадгалж байна...' : isEdit ? 'Хадгалах' : 'Нэмэх'}
              </button>
            </div>
          </form>
        )}
      </div>
    </AppLayout>
  );
}
